import { ref, computed } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { logger } from '../utils/logger.util';
/**
 * Composable para manejar el countdown de un tiempo en segundos.
 * @returns Objeto con el estado y funciones del composable:
 * - `countdownValue`: Ref reactivo con el valor del countdown en segundos
 * - `countdownMinutes`: Ref reactivo con el valor del countdown en minutos
 * - `countdownSeconds`: Ref reactivo con el valor del countdown en segundos
 * - `isFinished`: Ref reactivo que indica si el countdown ha finalizado
 * - `stopCountdown`: Función para detener el countdown
 * - `resumeCountdown`: Función para reiniciar el countdown
 * - `initCountdown`: Función para inicializar el countdown
 * - `finishCountdown`: Función para dar el countdown por terminado sin esperar a que llegue a cero
 */
export function useCountdown() {
  const countdownValue = ref(0);
  const isFinished = ref(false);

  const { pause, resume } = useIntervalFn(() => {
    if (countdownValue.value > 0) {
      countdownValue.value--;
    } else {
      isFinished.value = true;
      pause();
    }
  }, 1000);

  function initCountdown(seconds: number) {
    if (seconds <= 0) logger.warn('El countdown necesita una duración mayor a 0', { seconds });

    countdownValue.value = seconds;
    isFinished.value = false;
    resume();
  };

  function finishCountdown() {
    pause();
    countdownValue.value = 0;
    isFinished.value = true;
  }

  const countdownMinutes = computed(
    () => Math.floor(countdownValue.value / 60).toString().padStart(2, '0')
  );
  const countdownSeconds = computed(
    () => (countdownValue.value % 60).toString().padStart(2, '0')
  );

  return {
    countdownValue,
    countdownMinutes,
    countdownSeconds,
    isFinished,
    stopCountdown: pause,
    resumeCountdown: resume,
    initCountdown,
    finishCountdown,
  };
}
